import React, { useMemo, useState, useContext, useCallback } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { editable, media } from '@ubeya/shared-web/style';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import { Button as BaseButton, FlexColumn, Table } from '@ubeya/shared-web/components';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import ChipRenderer from './ChipRenderer';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  min-height: 90vh;
  padding-top: 20px;
  ${media.mobile`min-height: unset;`}
  .data-grid {
    box-shadow: none !important;
    border: 0 !important;
  }
`;

const StyledTable = styled(Table)`
  .header {
    border-right: 0 !important;
  }
  .row.header-row {
    background-color: ${({ theme }) => theme.colors.white};
  }

  .header-row {
    border-bottom: 0 !important;
  }

  .cell.header {
    font-size: 15px !important;
  }

  .cell {
    font-size: 14px !important;
  }
  .row {
    ${editable}
    border-top: 2px solid ${({ theme }) => theme.colors.grey2} !important;
    border-bottom: 2px solid ${({ theme }) => theme.colors.grey2} !important;
  }

  .sub-row {
    background-color: ${({ theme }) => theme.colors.white};
    cursor: default !important;
  }
`;

const Footer = styled.div`
  display: flex;
  padding: 0 15px 20px;
`;
const CancelButton = styled.div`
  display: flex;
  flex: 1;
`;

const GroupButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Button = styled(BaseButton)`
  padding-left: 80px;
  padding-right: 80px;
  margin-right: 40px;
`;

const SwapSideBarTable = ({ onClose, data, onApprove, onDecline }) => {
  const { id: requestId } = data || {};
  const { srcEmployee, srcSlot, dstEmployee, dstSlot, employeeReason } = data?.data || {};

  const { shortDateFormat, timeFormat } = useContext(ConfigContext);

  const { t } = useContext(ConfigContext);
  const [sort, setSort] = useState({ sortBy: 'name', sortDirection: 'asc' });
  const { mappedPositions } = usePositions();

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'creatorType',
        Cell: ChipRenderer,
        sortable: false
      },
      {
        Header: t('name'),
        dataKey: 'name',
        columnData: { mappedPositions, hideDeleted: true }
      },
      {
        Header: t('shiftToSwap'),
        dataKey: 'shiftToSwap',
        sortable: false
      },
      {
        Header: t('reason'),
        dataKey: 'employeeReason',
        formatter: (value) => t(value),
        sortable: false
      }
    ],
    [mappedPositions, t]
  );

  const getShiftName = useCallback(
    ({ startTime, endTime }) =>
      `${moment(startTime, API_DATE_TIME_FORMAT).format(shortDateFormat)}, ${[
        moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat),
        endTime && moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)
      ].join('-')}`,
    [shortDateFormat, timeFormat]
  );
  const tableData = useMemo(
    () => [
      {
        creatorType: 'initiator',
        name: [srcEmployee.firstName, srcEmployee.lastName].join(' '),
        shiftToSwap: getShiftName({ startTime: srcSlot.startTime, endTime: srcSlot.endTime }),
        employeeReason
      },
      {
        creatorType: 'acceptor',
        name: [dstEmployee.firstName, dstEmployee.lastName].join(' '),
        shiftToSwap: getShiftName({ startTime: dstSlot.startTime, endTime: dstSlot.endTime })
      }
    ],
    [
      srcEmployee.firstName,
      srcEmployee.lastName,
      getShiftName,
      srcSlot.startTime,
      srcSlot.endTime,
      employeeReason,
      dstEmployee.firstName,
      dstEmployee.lastName,
      dstSlot.startTime,
      dstSlot.endTime
    ]
  );

  const decline = useCallback(async () => {
    await onDecline({ requestIds: [requestId] });
    onClose();
  }, [onClose, onDecline, requestId]);

  const approve = useCallback(async () => {
    await onApprove(requestId);
    onClose();
  }, [onClose, onApprove, requestId]);

  return (
    <Wrapper>
      <StyledTable
        virtualized
        columns={columns}
        data={tableData}
        sortBy={sort.sortBy}
        sortDirection={sort.sortDirection}
        setSort={setSort}
        sortable
      />

      <Footer>
        <CancelButton>
          <Button onClick={onClose} secondary>
            {t('cancel')}
          </Button>
        </CancelButton>
        <GroupButtons>
          <Button onClick={decline} secondary>
            {t('decline')}
          </Button>
          <Button onClick={approve} primary>
            {t('approve')}
          </Button>
        </GroupButtons>
      </Footer>
    </Wrapper>
  );
};

export default SwapSideBarTable;
